import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, Edit, Trash2, MoreVertical, GripVertical, Clock, User, LayoutList, Columns3, IndianRupee, FileSpreadsheet, Loader2, Globe, History, Lock, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { Autocomplete } from '../components/Autocomplete';
import { useConfirmation } from '../components/ConfirmationProvider';
import { formatDate } from '../lib/formatUtils';
import { useAuth } from '../context/AuthContext';
import { exportToExcel } from '../lib/exportUtils';
import { usePageSize } from '../context/PageSizeContext';
import { Pagination } from '../components/Pagination';
import TaskAttachmentsField from '../components/tasks/TaskAttachmentsField';
import TaskCardThumbnails from '../components/tasks/TaskCardThumbnails';
import TaskViewDrawer from '../components/tasks/TaskViewDrawer';
import TasksFilterBar from '../components/tasks/TasksFilterBar';
import { EyeOff, Eye } from 'lucide-react';

const columns = [
  { id: 'todo', title: 'To Do', color: 'border-blue-500/50', bg: 'bg-blue-500/5' },
  { id: 'in_progress', title: 'In Progress', color: 'border-primary/50', bg: 'bg-primary/5' },
  { id: 'review', title: 'Review', color: 'border-amber-500/50', bg: 'bg-amber-500/5' },
  { id: 'done', title: 'Done', color: 'border-emerald-500/50', bg: 'bg-emerald-500/5' },
];

const priorityOptions = ['critical', 'high', 'medium', 'low'];

const getPriorityBadge = (p) => {
  const cfg = { critical: 'bg-red-500/10 text-red-500', high: 'bg-orange-500/10 text-orange-500', medium: 'bg-amber-500/10 text-amber-500', low: 'bg-gray-500/10 text-gray-400' };
  return <Badge className={`text-[10px] ${cfg[p] || cfg.medium}`}>{p}</Badge>;
};

export const TasksPage = () => {
  const confirm = useConfirmation();
  const { user, hasPermission } = useAuth();
  const { pageSize } = usePageSize();
  const isSystemUser = !user?.is_employee;

  const canCreate = hasPermission('tasks.create');
  const canEditAny = hasPermission('tasks.edit');
  const canDeleteAny = hasPermission('tasks.delete');
  const canViewAll = hasPermission('tasks.view_all') || hasPermission('*');
  const canEditAll = hasPermission('tasks.edit_all') || hasPermission('*');

  const userIdentifiers = React.useMemo(() => {
    if (!user) return new Set();
    const ids = new Set();
    if (user.id) ids.add(user.id);
    if (user.employee_id) ids.add(user.employee_id);
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    if (fullName) ids.add(fullName);
    return ids;
  }, [user]);

  const ownsTask = useCallback((task) => {
    if (!task || !user) return false;
    if (task.created_by === user.id) return true;
    return userIdentifiers.has(task.assigned_to || '');
  }, [user, userIdentifiers]);

  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState('mine'); // 'mine' | 'all'
  const [exporting, setExporting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [viewMode, setViewMode] = useState('kanban');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTask, setHistoryTask] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', project_name: '', assigned_to: '', assigned_to_name: '', priority: 'medium', status: 'todo', due_date: '', task_value: '' });
  // Files queued in the Create dialog before the task exists. Uploaded
  // sequentially right after the POST /tasks response.
  const [pendingAttachments, setPendingAttachments] = useState([]);
  // Task view drawer — opened by clicking the card body (not the ⋮
  // menu, which keeps Edit/Delete/History as direct actions).
  const [viewTask, setViewTask] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  // Filter bar state (assignee multi-select + due-date range + completed-date range).
  // Persisted in the URL so filters survive refresh + can be shared.
  const [assigneeFilter, setAssigneeFilter] = useState([]);   // array of user ids
  const [dueFrom, setDueFrom] = useState('');
  const [dueTo, setDueTo] = useState('');
  const [completedFrom, setCompletedFrom] = useState('');
  const [completedTo, setCompletedTo] = useState('');
  // "Show all completed" — flips the Done column's 14-day auto-hide off
  // so operators can peek at the historical tail without leaving Kanban.
  const [showAllCompleted, setShowAllCompleted] = useState(false);
  const AUTO_HIDE_DAYS = 14;   // decision 1a — Jira/Asana default

  const openViewDrawer = (task) => {
    setViewTask(task);
    setIsViewOpen(true);
  };

  const fetchTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: String(viewMode === 'list' ? page : 1),
        limit: String(viewMode === 'list' ? pageSize : 1000),
      });
      // Default scope: 'mine'. Only when an admin explicitly switches to
      // 'all' do we ask the backend for the global view.
      // Feb 2026 bug fix: when the operator explicitly picks someone
      // OTHER than themselves in the Assignee filter, an implicit
      // `mine=true` would AND with `assigned_to=<other>` on the backend
      // and always return 0 hits. Treat that as an implicit "All" scope
      // (for admins with view-all permission) so the filter is useful.
      const pickingOthers = assigneeFilter.length > 0
        && !assigneeFilter.every(id => userIdentifiers.has(id));
      if ((scope === 'all' || (pickingOthers && canViewAll))) {
        params.set('scope', 'all');
      } else {
        params.set('mine', 'true');
      }
      // Kanban auto-hide — hide Done tasks older than the window
      // (Jira/Linear style). List view shows everything so operators
      // can still reach the historical tail. `showAllCompleted` flips
      // the auto-hide off when the operator wants a full audit view.
      if (viewMode === 'kanban' && !showAllCompleted) {
        params.set('completed_within_days', String(AUTO_HIDE_DAYS));
      }
      // Filter bar
      if (assigneeFilter.length) params.set('assigned_to', assigneeFilter.join(','));
      if (dueFrom) params.set('due_from', dueFrom);
      if (dueTo) params.set('due_to', dueTo);
      if (completedFrom) params.set('completed_from', completedFrom);
      if (completedTo) params.set('completed_to', completedTo);
      const res = await api.get(`/tasks?${params.toString()}`);
      if (res.data.success) {
        setTasks(res.data.data);
        setTotal(res.data.total || 0);
      }
    } catch {
      setTasks([]);
      setTotal(0);
    }
  }, [viewMode, page, pageSize, scope, canViewAll, assigneeFilter, dueFrom, dueTo, completedFrom, completedTo, showAllCompleted]);

  useEffect(() => { setPage(1); }, [viewMode, scope, assigneeFilter, dueFrom, dueTo, completedFrom, completedTo, showAllCompleted]);
  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const task = tasks.find(t => t.id === draggableId);
    if (!task) return;
    if (!(canEditAll || (canEditAny && ownsTask(task)))) {
      toast.error('You can only move tasks assigned to you.');
      return;
    }

    // Optimistic update
    setTasks(prev => prev.map(t => t.id === draggableId ? { ...t, status: destination.droppableId } : t));

    if (source.droppableId !== destination.droppableId) {
      try {
        // Use the dedicated status endpoint — keeps history clean and avoids
        // accidentally clobbering other fields with stale client state.
        await api.put(`/tasks/${draggableId}/status`, { status: destination.droppableId });
        const col = columns.find(c => c.id === destination.droppableId);
        toast.success(`Moved to ${col?.title || destination.droppableId}`);
      } catch (err) {
        setTasks(prev => prev.map(t => t.id === draggableId ? { ...t, status: source.droppableId } : t));
        toast.error(err.response?.data?.detail || 'Failed to update task status');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSystemUser && formData.assigned_to && !formData.task_value) {
      toast.error('Task Value (INR) is required when assigning to an employee');
      return;
    }
    const payload = { ...formData };
    if (payload.task_value) payload.task_value = parseFloat(payload.task_value);
    else payload.task_value = 0;
    try {
      let savedTask;
      if (isEditing && selectedTask) {
        const res = await api.put(`/tasks/${selectedTask.id}`, payload);
        savedTask = res.data?.data || selectedTask;
        toast.success('Task updated');
      } else {
        const res = await api.post('/tasks', payload);
        savedTask = res.data?.data;
        toast.success('Task created');
      }
      // Drain pending attachments queued in the dialog. Failures here
      // just surface a toast — the task itself was saved successfully.
      if (!isEditing && savedTask?.id && pendingAttachments.length > 0) {
        let uploaded = 0;
        for (const file of pendingAttachments) {
          try {
            const fd = new FormData();
            fd.append('file', file);
            await api.post(`/tasks/${savedTask.id}/attachments`, fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            uploaded += 1;
          } catch (err) {
            toast.error(err?.response?.data?.detail || `Failed to attach "${file.name}"`);
          }
        }
        if (uploaded > 0) toast.success(`${uploaded} attachment${uploaded > 1 ? 's' : ''} added`);
      }
      fetchTasks();
    } catch { toast.error('Operation failed'); }
    setIsDialogOpen(false);
    resetForm();
  };

  const handleDelete = async (task) => {
    if (!(canEditAll || (canDeleteAny && task.created_by === user?.id))) {
      toast.error('Only the task creator (or a manager) can delete this task.');
      return;
    }
    const confirmed = await confirm({ title: 'Delete Task', description: `Are you sure you want to delete "${task.title}"? This action cannot be undone.` });
    if (!confirmed) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      toast.success('Task deleted');
      fetchTasks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const openHistory = async (task) => {
    setHistoryTask(task);
    setHistoryOpen(true);
    setHistoryRows([]);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/tasks/${task.id}/history`);
      if (res.data.success) setHistoryRows(res.data.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load history'); }
    finally { setHistoryLoading(false); }
  };

  const handleEdit = (task) => {
    setSelectedTask(task);
    setFormData({ title: task.title, description: task.description || '', project_name: task.project_name || '', assigned_to: task.assigned_to || '', assigned_to_name: task.assigned_to_name || task.assigned_to || '', priority: task.priority, status: task.status, due_date: task.due_date ? task.due_date.split('T')[0] : '', task_value: task.task_value != null ? String(task.task_value) : '' });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const resetForm = () => { setFormData({ title: '', description: '', project_name: '', assigned_to: '', assigned_to_name: '', priority: 'medium', status: 'todo', due_date: '', task_value: '' }); setSelectedTask(null); setIsEditing(false); setPendingAttachments([]); };

  const getColumnTasks = (colId) => tasks.filter(t => t.status === colId);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get('/tasks?limit=10000');
      const rows = res.data?.data || [];
      if (!rows.length) { toast.info('No tasks to export'); return; }
      exportToExcel(rows.map(t => ({
        title: t.title,
        project_name: t.project_name || '',
        assigned_to: t.assigned_to_name || t.assigned_to || '',
        priority: t.priority,
        status: t.status,
        due_date: t.due_date || '',
        task_value: t.task_value || 0,
      })), [
        { key: 'title', header: 'Title' },
        { key: 'project_name', header: 'Project' },
        { key: 'assigned_to', header: 'Assigned To' },
        { key: 'priority', header: 'Priority' },
        { key: 'status', header: 'Status' },
        { key: 'due_date', header: 'Due Date' },
        { key: 'task_value', header: 'Value (INR)' },
      ], 'Tasks');
      toast.success(`Exported ${rows.length} tasks`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tasks-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            Tasks
            <Badge variant="outline" className="text-xs font-normal capitalize" data-testid="tasks-scope-label">
              {scope === 'all' ? 'All tasks' : 'My tasks'}
            </Badge>
          </h1>
          <p className="text-muted-foreground">{scope === 'all' ? 'Manager view — showing every task in the system.' : 'Tasks assigned to you or created by you.'} Drag and drop to manage workflow.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canViewAll && (
            <div className="flex rounded-lg border border-border overflow-hidden text-xs" role="tablist" aria-label="Task scope">
              <button onClick={() => setScope('mine')} className={`px-3 py-1.5 inline-flex items-center gap-1 ${scope === 'mine' ? 'bg-primary text-white' : 'hover:bg-muted/40'}`} data-testid="scope-mine"><User className="h-3.5 w-3.5" />Mine</button>
              <button onClick={() => setScope('all')} className={`px-3 py-1.5 inline-flex items-center gap-1 ${scope === 'all' ? 'bg-primary text-white' : 'hover:bg-muted/40'}`} data-testid="scope-all"><Globe className="h-3.5 w-3.5" />All</button>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} data-testid="export-tasks-excel">
            {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
            Excel
          </Button>
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button onClick={() => setViewMode('kanban')} className={`px-3 py-1.5 text-xs ${viewMode === 'kanban' ? 'bg-primary text-white' : 'hover:bg-muted/40'}`} data-testid="view-kanban"><Columns3 className="h-3.5 w-3.5" /></button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 text-xs ${viewMode === 'list' ? 'bg-primary text-white' : 'hover:bg-muted/40'}`} data-testid="view-list"><LayoutList className="h-3.5 w-3.5" /></button>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) resetForm(); }}>
            {canCreate && (<DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-task-btn"><Plus className="h-4 w-4 mr-2" />Add Task</Button></DialogTrigger>)}
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{isEditing ? 'Edit Task' : 'Create Task'}</DialogTitle><DialogDescription>Enter task details</DialogDescription></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2 col-span-2"><Label>Title *</Label><Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} required data-testid="task-title" /></div>
                  <div className="space-y-2 col-span-2">
                    <Label>Description</Label>
                    <Textarea
                      rows={3}
                      value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Add context, links, acceptance criteria…"
                      data-testid="task-description"
                    />
                  </div>
                  <div className="space-y-2"><Label>Project</Label>
                    <Autocomplete
                      value={formData.project_name}
                      onChange={v => setFormData({ ...formData, project_name: v })}
                      fetchOptions={async (q) => {
                        const res = await api.get(`/projects?search=${q}&limit=10`);
                        return res.data.success ? res.data.data.map(p => p.name) : [];
                      }}
                      placeholder="Search projects..."
                      data-testid="task-project"
                    />
                  </div>
                  <div className="space-y-2"><Label>Assigned To</Label>
                    <Autocomplete
                      value={formData.assigned_to}
                      displayValue={formData.assigned_to_name || ''}
                      onChange={(val, item) => setFormData({
                        ...formData,
                        assigned_to: val,
                        // Capture the label so the input keeps rendering
                        // the human-readable name after selection (parent
                        // only stores the id).
                        assigned_to_name: item?.label || '',
                      })}
                      fetchOptions={async (q) => {
                        const res = await api.get(`/employees?search=${q}&status=active&limit=10`);
                        if (!res.data.success) return [];
                        // Object-mode — label shown, user_id UUID stored.
                        // Filter out employees without a linked user_id so
                        // downstream ownership checks can't be tricked by
                        // an unlinked employee.
                        return res.data.data
                          .filter(e => e.user_id)
                          .map(e => ({
                            value: e.user_id,
                            label: `${e.first_name} ${e.last_name || ''}`.trim(),
                          }));
                      }}
                      fetchDefaults={async () => {
                        const res = await api.get('/smb/users/recently-assigned?limit=5');
                        if (!res.data.success) return [];
                        return res.data.data
                          .filter(u => u.id || u.user_id)
                          .map(u => ({
                            value: u.user_id || u.id,
                            label: `${u.first_name} ${u.last_name || ''}`.trim(),
                          }));
                      }}
                      placeholder="Search employees..."
                      data-testid="task-assigned"
                    />
                  </div>
                  <div className="space-y-2"><Label>Priority</Label><Select value={formData.priority} onValueChange={v => setFormData({ ...formData, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{priorityOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-2"><Label>Status</Label><Select value={formData.status} onValueChange={v => setFormData({ ...formData, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{columns.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-2 col-span-2"><Label>Due Date</Label><Input type="date" value={formData.due_date} onChange={e => setFormData({ ...formData, due_date: e.target.value })} /></div>
                  {isSystemUser && (
                    <div className="space-y-2 col-span-2"><Label>Task Value (INR) {formData.assigned_to && <span className="text-red-400">*</span>}</Label><Input type="number" min="0" step="0.01" value={formData.task_value} onChange={e => setFormData({ ...formData, task_value: e.target.value })} placeholder="Monetary value of this task" data-testid="task-value" /></div>
                  )}
                  <div className="col-span-2">
                    {/* Live mode when editing (taskId known); draft mode while
                        creating. The dialog's submit handler drains the draft
                        queue right after POST /tasks succeeds. */}
                    <TaskAttachmentsField
                      taskId={isEditing ? selectedTask?.id : null}
                      pendingFiles={pendingAttachments}
                      onPendingChange={setPendingAttachments}
                    />
                  </div>
                </div>
                <DialogFooter><Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button><Button type="submit" data-testid="task-submit-btn">{isEditing ? 'Update' : 'Create'}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filter bar — always visible above the board (decision 4a) */}
      <TasksFilterBar
        currentUser={user}
        assigneeFilter={assigneeFilter} setAssigneeFilter={setAssigneeFilter}
        dueFrom={dueFrom} setDueFrom={setDueFrom} dueTo={dueTo} setDueTo={setDueTo}
        completedFrom={completedFrom} setCompletedFrom={setCompletedFrom}
        completedTo={completedTo} setCompletedTo={setCompletedTo}
      />

      {/* Kanban Board */}
      {viewMode === 'kanban' && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kanban-board">
            {columns.map(col => {
              const colTasks = getColumnTasks(col.id);
              const isDone = col.id === 'done';
              return (
                <div key={col.id} className={`rounded-lg border-t-2 ${col.color} ${col.bg} p-3`}>
                  <div className="flex items-center justify-between mb-3 gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <h3 className="font-semibold text-sm">{col.title}</h3>
                      <Badge variant="outline" className="text-xs">{colTasks.length}</Badge>
                    </div>
                    {isDone && (
                      <button
                        type="button"
                        onClick={() => setShowAllCompleted(v => !v)}
                        className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-border hover:bg-muted/40 text-muted-foreground shrink-0"
                        title={showAllCompleted
                          ? `Hide tasks completed more than ${AUTO_HIDE_DAYS} days ago`
                          : `Currently hiding tasks completed >${AUTO_HIDE_DAYS} days ago — click to show all`}
                        data-testid="show-all-completed-toggle"
                      >
                        {showAllCompleted
                          ? (<><EyeOff className="h-3 w-3" /> Hide old</>)
                          : (<><Eye className="h-3 w-3" /> Show all</>)}
                      </button>
                    )}
                  </div>
                  <Droppable droppableId={col.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`space-y-2 min-h-[120px] rounded-md transition-colors ${snapshot.isDraggingOver ? 'bg-primary/5' : ''}`}
                        data-testid={`column-${col.id}`}
                      >
                        {colTasks.map((task, index) => {
                          const isMine = ownsTask(task);
                          const isCreator = task.created_by === user?.id;
                          const canMove = canEditAll || (canEditAny && isMine);
                          const canEditCard = canEditAll || (canEditAny && isMine);
                          const canDeleteCard = canEditAll || (canDeleteAny && isCreator);
                          return (
                          <Draggable key={task.id} draggableId={task.id} index={index} isDragDisabled={!canMove}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={`bg-card border ${isMine ? 'border-l-4 border-l-primary' : 'border-border'} rounded-lg p-3 transition-shadow ${snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/20' : 'hover:shadow-md'} ${!canMove ? 'opacity-90' : ''}`}
                                data-testid={`task-card-${task.id}`}
                              >
                                <div className="flex items-start gap-2">
                                  {canMove ? (
                                    <div {...provided.dragHandleProps} className="mt-0.5 cursor-grab active:cursor-grabbing" title="Drag to change status">
                                      <GripVertical className="h-4 w-4 text-muted-foreground/50" />
                                    </div>
                                  ) : (
                                    <div className="mt-0.5" title="You can't change this task's status">
                                      <Lock className="h-3.5 w-3.5 text-muted-foreground/40" />
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => openViewDrawer(task)}
                                    className="flex-1 min-w-0 text-left"
                                    data-testid={`task-card-open-${task.id}`}
                                  >
                                    <p className="font-medium text-sm">{task.title}</p>
                                    {task.description && (
                                      <p
                                        className="text-xs text-muted-foreground/90 mt-0.5 line-clamp-2 whitespace-pre-wrap break-words"
                                        title={task.description}
                                        data-testid={`task-description-${task.id}`}
                                      >
                                        {task.description}
                                      </p>
                                    )}
                                    <p className="text-xs text-muted-foreground mt-0.5">{task.project_name}</p>
                                    <TaskCardThumbnails
                                      images={task.image_attachments || []}
                                      totalAttachments={task.attachment_count || 0}
                                      onOpenAt={() => openViewDrawer(task)}
                                      data_testid={`task-card-thumbs-${task.id}`}
                                    />
                                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                                      {getPriorityBadge(task.priority)}
                                      {task.due_date && (
                                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Clock className="h-3 w-3" />{formatDate(task.due_date)}</span>
                                      )}
                                      {task.attachment_count > 0 && (
                                        <span
                                          className="text-[10px] text-muted-foreground flex items-center gap-0.5"
                                          title={`${task.attachment_count} attachment${task.attachment_count > 1 ? 's' : ''}`}
                                          data-testid={`task-attach-count-${task.id}`}
                                        >
                                          <Paperclip className="h-3 w-3" />{task.attachment_count}
                                        </span>
                                      )}
                                    </div>
                                    {task.assigned_to && (
                                      <div className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground"><User className="h-3 w-3" />{task.assigned_to_name || task.assigned_to}</div>
                                    )}
                                    {isSystemUser && task.task_value > 0 && (
                                      <div className="flex items-center gap-0.5 mt-1 text-[10px] text-emerald-500 font-medium"><IndianRupee className="h-3 w-3" />{Number(task.task_value).toLocaleString('en-IN')}</div>
                                    )}
                                  </button>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild><button className="p-1 hover:bg-muted rounded" data-testid={`task-actions-${task.id}`}><MoreVertical className="h-3.5 w-3.5 text-muted-foreground" /></button></DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => openHistory(task)} data-testid={`task-history-${task.id}`}><History className="h-4 w-4 mr-2" />History</DropdownMenuItem>
                                      {canEditCard && (<DropdownMenuItem onClick={() => handleEdit(task)} data-testid={`task-edit-${task.id}`}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>)}
                                      {canDeleteCard && (<DropdownMenuItem onClick={() => handleDelete(task)} className="text-destructive" data-testid={`task-delete-${task.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </div>
                            )}
                          </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <Card><CardContent className="p-0">
          <div className="divide-y divide-border">
            {tasks.map(task => {
              const isMine = ownsTask(task);
              const isCreator = task.created_by === user?.id;
              const canEditCard = canEditAll || (canEditAny && isMine);
              const canDeleteCard = canEditAll || (canDeleteAny && isCreator);
              return (
              <div key={task.id} className={`flex items-center gap-4 p-4 hover:bg-muted/20 transition-colors ${isMine ? 'border-l-4 border-l-primary' : ''}`} data-testid={`task-list-${task.id}`}>
                <button
                  type="button"
                  onClick={() => openViewDrawer(task)}
                  className="flex-1 min-w-0 text-left"
                  data-testid={`task-list-open-${task.id}`}
                >
                  <p className="font-medium text-sm">{task.title}</p>
                  {task.description && (
                    <p className="text-xs text-muted-foreground/90 mt-0.5 line-clamp-2 whitespace-pre-wrap break-words"
                       title={task.description}>
                      {task.description}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">{task.project_name}</p>
                  <TaskCardThumbnails
                    images={task.image_attachments || []}
                    totalAttachments={task.attachment_count || 0}
                    onOpenAt={() => openViewDrawer(task)}
                    data_testid={`task-list-thumbs-${task.id}`}
                  />
                </button>
                <div className="flex items-center gap-3 shrink-0">
                  {getPriorityBadge(task.priority)}
                  <Badge variant="outline" className="text-[10px] capitalize">{task.status.replace('_', ' ')}</Badge>
                  {task.attachment_count > 0 && (
                    <span
                      className="text-[10px] text-muted-foreground flex items-center gap-0.5"
                      title={`${task.attachment_count} attachment${task.attachment_count > 1 ? 's' : ''}`}
                      data-testid={`task-list-attach-count-${task.id}`}
                    >
                      <Paperclip className="h-3 w-3" />{task.attachment_count}
                    </span>
                  )}
                  {task.assigned_to && <span className="text-xs text-muted-foreground hidden sm:block">{task.assigned_to_name || task.assigned_to}</span>}
                  {isSystemUser && task.task_value > 0 && <Badge className="bg-emerald-500/10 text-emerald-500 text-[10px]"><IndianRupee className="h-3 w-3 mr-0.5" />{Number(task.task_value).toLocaleString('en-IN')}</Badge>}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`task-list-actions-${task.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openHistory(task)} data-testid={`task-list-history-${task.id}`}><History className="h-4 w-4 mr-2" />History</DropdownMenuItem>
                      {canEditCard && (<DropdownMenuItem onClick={() => handleEdit(task)} data-testid={`task-list-edit-${task.id}`}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>)}
                      {canDeleteCard && (<DropdownMenuItem onClick={() => handleDelete(task)} className="text-destructive" data-testid={`task-list-delete-${task.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              );
            })}
          </div>
        </CardContent></Card>
      )}

      {viewMode === 'list' && (
        <Pagination
          page={page}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
          testIdPrefix="tasks-pagination"
        />
      )}

      {/* Task View Drawer — read-only detail, opens on card click */}
      <TaskViewDrawer
        open={isViewOpen}
        onOpenChange={(o) => { setIsViewOpen(o); if (!o) setViewTask(null); }}
        taskSummary={viewTask}
        canEdit={!!(viewTask && (canEditAll || (canEditAny && ownsTask(viewTask))))}
        onEdit={(t) => handleEdit(t)}
      />

      {/* History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg" data-testid="task-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-4 w-4" />Task History</DialogTitle>
            <DialogDescription>{historyTask?.title}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[420px] overflow-y-auto">
            {historyLoading ? (
              <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            ) : historyRows.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No history recorded yet.</p>
            ) : (
              <ol className="space-y-2">
                {historyRows.map((h, i) => (
                  <li key={i} className="flex items-start gap-3 rounded-md border p-2.5 bg-muted/20" data-testid={`task-history-row-${i}`}>
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      {h.event === 'created' ? <Plus className="h-3.5 w-3.5" /> : <History className="h-3.5 w-3.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">
                        {h.event === 'created' && <>Created with status <Badge variant="outline" className="text-[10px] capitalize ml-1">{(h.status || 'todo').replace('_', ' ')}</Badge></>}
                        {h.event === 'status_changed' && (
                          <>Moved from <Badge variant="outline" className="text-[10px] capitalize">{(h.from || '—').replace('_', ' ')}</Badge> to <Badge variant="outline" className="text-[10px] capitalize">{(h.to || '—').replace('_', ' ')}</Badge></>
                        )}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        by <span className="font-medium text-foreground">{h.by_name || h.by || 'Unknown'}</span> · {h.at ? new Date(h.at).toLocaleString() : ''}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
